import * as React from 'react';
import { Formik, FormikErrors } from 'formik';
import Row from 'src/frontend-common/components/Row';
import { useSignUpUserMutation } from 'src/redux/services/auth';
import countries from 'src/components/PhoneInput/countries';
import { styled, Typography, useTheme, Checkbox, Grid, TextField, MenuItem } from '@mui/material';
import { Heading, Title, CustomAlert, CustomTextField } from 'src/pages/Login/forms/styledFormComponents';
import PasswordFormField from 'src/pages/Login/components/PasswordFormField';
import Dpa from 'src/pages/Login/assets/Trustfull_DataProcessingAddendum.pdf';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import { CountryOption } from 'src/components/PhoneInput/CountriesDropdown';
import { useNavigate, useLocation } from 'react-router';
import ButtonComponent from 'src/components/ButtonComponent';
import AlreadyHaveAccount from 'src/pages/Login/components/BottomAction';
import { getCountryFromTimezone } from 'src/utils/timezoneToCountry';
import { useInitialPrefix } from 'src/hooks';

interface FormValues {
  username: string;
  password: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  company_name: string;
  company_size: string;
  usage_reason: string;
}

const TermsAndConditionRow = styled(Row)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  alignItems: 'flex-start',
  '& > span': {
    padding: theme.spacing(0, 1, 0, 0),
    '& path': {
      fill: 'rgba(0, 0, 0, 0.23)',
    },
    '&.Mui-checked': {
      '& path': {
        fill: 'black',
      },
    },
  },
  '& a': {
    color: theme.palette.primary.main,
  },
}));

const CustomType = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const GridWrapper = styled(Grid)(() => ({
  '& .MuiFormControl-root': {
    marginBottom: 0,
  },
}));

const PhoneErrorWrapper = styled('div')(() => ({
  fontWeight: 400,
  fontSize: '0.75rem',
  color: '#F44336',
  lineHeight: 1.66,
  letterSpacing: '0.03333em',
  textAlign: 'left',
  marginTop: 3,
  marginRight: 14,
  marginBottom: 0,
  marginLeft: 14,
}));
export const createInitialPrefix = () => {
  const language = navigator.language.split('-').reverse()[0]?.toLowerCase();
  const initialCountry = countries.find((el) => el.iso2 === language);
  if (initialCountry) {
    return initialCountry as CountryOption;
  }
  return undefined;
};

const SignupForm: React.FC = () => {
  const [signupUser, { isLoading, isError }] = useSignUpUserMutation();
  const [isValidPwd, setIsValidPwd] = React.useState<boolean>(false);
  const sizeListMap = [
    { id: '1-10', name: '1-10 employees' },
    { id: '11-50', name: '11-50 employees' },
    { id: '51-200', name: '51-200 employees' },
    { id: '201-500', name: '201-500 employees' },
    { id: '501-1000', name: '501-1000 employees' },
    { id: '1001-5000', name: '1001-5000 employees' },
    { id: '5001-10,000', name: '5001-10,000 employees' },
    { id: '10,001+', name: '10,001+ employees' },
  ];
  const usageListMap = [
    { id: 'payment_fraud', name: 'Payment Fraud' },
    { id: 'identity_verification', name: 'Identity Verification' },
    { id: 'transaction_risk_analysis', name: 'Transaction Risk Analysis' },
    { id: 'aml', name: 'AML' },
    { id: 'credit_risk_assesment', name: 'Credit Risk Assesment' },
    { id: 'data_enrichment', name: 'Data Enrichment' },
    { id: 'other', name: 'Other' },
  ];

  const [termsAndConditionAccepted, setTermsAndConditionAccepted] = React.useState<boolean>(false);
  const initialPrefix = useInitialPrefix();
  const termsAndConditionsHandler = () => {
    setTermsAndConditionAccepted((prev) => !prev);
  };

  const navigate = useNavigate();
  const theme = useTheme();
  const urlParams = new URLSearchParams(window.location.search);
  const email = urlParams.get('email');
  const location = useLocation();
  return (
    <>
      <Heading style={{ display: 'block' }}>
        <Title
          style={{
            textAlign: 'initial',
            fontSize: 40,
            fontWeight: 600,
            marginTop: theme.spacing(5),
          }}
        >
          Start Your Free Trial!
        </Title>
      </Heading>
      <Formik
        initialValues={{
          username: email || '',
          password: '',
          first_name: '',
          last_name: '',
          phone_number: '',
          phone_country_code: getCountryFromTimezone() || '',
          company_name: '',
          company_size: '',
          usage_reason: '',
          __all: null,
        }}
        validateOnChange={true}
        validate={(values) => {
          const errors: FormikErrors<FormValues> = {};

          if (values.username.length === 0) {
            errors.username = 'This field is required';
          }
          if (values.password.length === 0) {
            errors.password = 'This field is required';
          }
          if (values.password.length > 0 && !isValidPwd) {
            errors.password = 'Incorrect password';
          }
          if (values.first_name.length === 0) {
            errors.first_name = 'This field is required';
          }

          if (values.last_name.length === 0) {
            errors.last_name = 'This field is required';
          }
          if (!values.phone_number || values.phone_number.trim().length < 3) {
            errors.phone_number = 'Invalid phone number';
          }
          if (values.phone_number.trim().length === 0) {
            errors.phone_number = 'Required';
          }

          if (values.company_name.length === 0) {
            errors.company_name = 'This field is required';
          }
          if (values.company_size.length === 0) {
            errors.company_size = 'This field is required';
          }
          if (values.usage_reason.length === 0) {
            errors.usage_reason = 'This field is required';
          }

          return errors;
        }}
        onSubmit={async (values, { setErrors }) => {
          const result = await signupUser({
            username: values.username,
            password: values.password,
            first_name: values.first_name,
            last_name: values.last_name,
            phone_number: `+${values.phone_number}`,
            phone_country_code: `+${values.phone_country_code}`,
            company_name: values.company_name,
            company_size: values.company_size,
            usage_reason: values.usage_reason,
          });

          if ('error' in result && 'data' in result.error) {
            const violations = // @ts-ignore:next-line
              result.error?.data?.violations as FormikErrors<FormValues>;
            setErrors({ ...violations });
            return;
          }

          // Get the current query parameters
          const searchParams = new URLSearchParams(location.search);
          navigate('/auth/thanks?' + searchParams.toString(), {
            state: { username: values.username },
          });
        }}
      >
        {/* eslint-disable-next-line complexity */}
        {(formikProps) => {
          const {
            errors: {
              username: usernameError,
              password: passwordError,
              first_name: firstNameError,
              last_name: lastNameError,
              company_name: companyNameError,
              company_size: companySizeError,
              usage_reason: useCaseError,
              __all: genericErrors,
            },
            touched: {
              username: usernameTouched,
              password: passwordTouched,
              first_name: firstNameTouched,
              last_name: lastNameTouched,
              company_name: companyNameTouched,
              company_size: companySizeTouched,
              usage_reason: useCaseTouched,
              phone_number: phoneNumberTouched,
            },
          } = formikProps;
          return (
            <>
              {isError && <CustomAlert severity="error">{genericErrors}</CustomAlert>}

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  formikProps.handleSubmit();
                }}
              >
                <GridWrapper container md={12} xs={12} spacing={3}>
                  <Grid item md={12} xs={12}>
                    <CustomTextField
                      variant="outlined"
                      label="Work email"
                      error={usernameTouched && !!usernameError}
                      helperText={usernameTouched && usernameError}
                      inputProps={{ 'data-fido-field': 'email' }}
                      {...formikProps.getFieldProps('username')}
                    />
                  </Grid>

                  <>
                    <Grid item md={12} xs={12}>
                      <PasswordFormField
                        passwordError={passwordError}
                        passwordTouched={passwordTouched}
                        fieldProps={formikProps.getFieldProps('password')}
                        requirements
                        setIsValidPwd={setIsValidPwd}
                        showStrength={true}
                      />
                    </Grid>

                    <Grid item md={6} xs={12}>
                      <CustomTextField
                        variant="outlined"
                        fullWidth={true}
                        label="First name"
                        error={firstNameTouched && !!firstNameError}
                        helperText={firstNameTouched && firstNameError}
                        {...formikProps.getFieldProps('first_name')}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <CustomTextField
                        variant="outlined"
                        fullWidth={true}
                        label="Last name"
                        error={lastNameTouched && !!lastNameError}
                        helperText={lastNameTouched && lastNameError}
                        {...formikProps.getFieldProps('last_name')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <>
                        <PhoneInput
                          dropdownStyle={{ position: 'fixed' }}
                          country={initialPrefix.toLowerCase() || formikProps.getFieldProps('phone_country_code').value}
                          enableSearch
                          value={formikProps.getFieldProps('phone_number').value}
                          isValid={phoneNumberTouched && !formikProps.errors['phone_number']}
                          onChange={(phone, obj: CountryData) => {
                            formikProps.setFieldTouched('phone_number', true);
                            formikProps.setFieldValue('phone_country_code', obj.dialCode, true);
                            formikProps.setFieldValue('phone_number', phone, true);
                          }}
                          inputStyle={{ width: '100%', height: 53 }}
                        />
                        {formikProps.errors['phone_number'] && (
                          <PhoneErrorWrapper>{phoneNumberTouched && formikProps.errors['phone_number']}</PhoneErrorWrapper>
                        )}
                      </>
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <CustomTextField
                        variant="outlined"
                        fullWidth={true}
                        label="Company name"
                        error={companyNameTouched && !!companyNameError}
                        helperText={companyNameTouched && companyNameError}
                        {...formikProps.getFieldProps('company_name')}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextField
                        id="company_size-select"
                        select
                        fullWidth={true}
                        error={companySizeTouched && !!companySizeError}
                        helperText={companySizeTouched && companySizeError}
                        label="Company Size"
                        value={formikProps.getFieldProps('company_size').value}
                        onChange={(event) => {
                          formikProps.setFieldValue('company_size', event.target.value, true);
                        }}
                      >
                        {sizeListMap.map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextField
                        id="usage_reason-select"
                        select
                        label="Use Case"
                        error={useCaseTouched && !!useCaseError}
                        helperText={useCaseTouched && useCaseError}
                        fullWidth={true}
                        value={formikProps.getFieldProps('usage_reason').value}
                        onChange={(event) => {
                          formikProps.setFieldValue('usage_reason', event.target.value, true);
                        }}
                      >
                        {usageListMap.map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </>

                  <Grid item md={12} xs={12}>
                    <TermsAndConditionRow>
                      <>
                        <Checkbox checked={termsAndConditionAccepted} onChange={termsAndConditionsHandler} color="error" />
                        <CustomType variant="body2">
                          By registering, you confirm you have read and agree to the{' '}
                          <a
                            href="https://14555036.fs1.hubspotusercontent-na1.net/hubfs/14555036/Legal/Master_Service_Agreement_(EN).pdf"
                            target="_blank"
                            rel="noreferral noreferrer"
                            style={{ color: 'black' }}
                          >
                            Master Service Agreement
                          </a>{' '}
                          and the{' '}
                          <a href={Dpa} target="_blank" rel="noreferral noreferrer" style={{ color: 'black' }}>
                            DPA
                          </a>
                        </CustomType>
                      </>
                    </TermsAndConditionRow>
                  </Grid>
                  <Grid item md={12} xs={12} style={{ paddingTop: 0 }}>
                    <ButtonComponent
                      data-fido-field="submit"
                      variant="contained"
                      buttontype="mainaction"
                      text={isLoading ? 'Loading...' : 'Start your free trial'}
                      sx={{ marginBottom: theme.spacing(3) }}
                      style={{
                        backgroundColor: isLoading || !termsAndConditionAccepted || Object.keys(formikProps.errors).length > 0 ? '' : 'black',
                        color: 'white',
                        width: '100%',
                        height: 56,
                      }}
                      disableElevation
                      disabled={isLoading || !termsAndConditionAccepted || Object.keys(formikProps.errors).length > 0}
                      onClick={() => {
                        formikProps.submitForm();
                      }}
                    />
                  </Grid>
                </GridWrapper>
              </form>

              <AlreadyHaveAccount hAlign="center" title="Already have an account?" bold="Sign in" onClick={() => navigate('/login')} />
            </>
          );
        }}
      </Formik>
    </>
  );
};

export default SignupForm;
